import { useEffect, useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useSessionStore } from '@/store/sessionStore';
import { useSystemPromptStore } from '@/store/systemPromptStore';
import { useContextMenuTemplateStore } from '@/store/contextTemplateStore';
import { useAgentStore } from '@/store/agentStore';
import { Card, CardContent } from "@/components/ui/card";
import { UpdateSessionPayload, ALL_REASONING_EFFORTS, ReasoningEffort } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Link, X, UserCog, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import ModelSelector from '@/app/components/common/ModelSelector';

const SessionSettings: React.FC = () => {
    const currentSessionId = useSessionStore(state => state.currentSessionId);
    const currentSessionDetails = useSessionStore(state => state.currentSessionDetails);
    const updateSessionDefaults = useSessionStore(state => state.updateSessionDefaults);
    const selectAgent = useSessionStore(state => state.selectAgent);
    const activeAgentId = useSessionStore(state => state.activeAgentId);
    const setActiveAgentId = useSessionStore(state => state.setActiveAgentId);

    const { systemPrompts, isSystemPromptsLoading } = useSystemPromptStore();
    const { templates: contextTemplates, isLoading: isTemplatesLoading } = useContextMenuTemplateStore();
    const { agents, fetchAgents } = useAgentStore();

    // Collapsed by default; click to expand
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    const handleDefaultsUpdate = async (payload: UpdateSessionPayload) => {
        if (!currentSessionId) return;
        await updateSessionDefaults(payload);
    };

    const handleSystemPromptChange = (value: string) => {
        const newSystemPromptId = value === 'none' ? null : value;
        handleDefaultsUpdate({ system_prompt_id: newSystemPromptId });
    };

    const handleAgentSelect = async (agentId: string) => {
        if (agentId === 'none') {
            setActiveAgentId(null);
            return;
        }
        const agent = agents.find(a => a.id === agentId);
        if (!agent) return;
        setActiveAgentId(agentId);
        await selectAgent(agent);
    };

    const handleReSyncAgent = async () => {
        if (!activeAgentId) return;
        const agent = agents.find(a => a.id === activeAgentId);
        if (!agent) return;
        await selectAgent(agent);
    };

    const handleDetachAgent = () => {
        setActiveAgentId(null);
    };

    const selectedSystemPromptId = useMemo(() => {
        if (!systemPrompts.length) return 'none';
        const defaultId = systemPrompts.find(p => p.is_default)?.id;
        return currentSessionDetails?.system_prompt_id || defaultId || 'none';
    }, [systemPrompts, currentSessionDetails?.system_prompt_id]);

    const selectedSystemPrompt = useMemo(() => {
        return systemPrompts.find(p => p.id === selectedSystemPromptId);
    }, [systemPrompts, selectedSystemPromptId]);

    const selectedModelId = useMemo(() => {
        return currentSessionDetails?.model_id || 'ollama/glm-5.1:cloud';
    }, [currentSessionDetails?.model_id]);

    const activeAgent = useMemo(() => {
        if (!activeAgentId) return null;
        return agents.find(a => a.id === activeAgentId) || null;
    }, [activeAgentId, agents]);

    // Build a one-line summary of current config for the collapsed state
    const configSummary = useMemo(() => {
        const parts: string[] = [];
        if (currentSessionDetails?.model_id) {
            const shortModel = currentSessionDetails.model_id.split('/').pop() || currentSessionDetails.model_id;
            parts.push(`Model: ${shortModel}`);
        }
        if (currentSessionDetails?.reasoning_effort) {
            parts.push(`Reasoning: ${currentSessionDetails.reasoning_effort}`);
        }
        const promptName = selectedSystemPrompt?.prompt_name || 'Default';
        parts.push(`Prompt: ${promptName}`);

        if (parts.length === 0) return 'Default configuration';
        return parts.join(' · ');
    }, [currentSessionDetails?.model_id, currentSessionDetails?.reasoning_effort, selectedSystemPrompt]);

    return (
        <Card>
            <CardContent className="p-2 space-y-1">
                {/* Agent Selector */}
                <div className="space-y-1">
                    <Label htmlFor="agent-selector" className="text-xs flex items-center gap-1">
                        <UserCog className="h-3 w-3" />
                        Agent Preset
                    </Label>
                    <div className="flex items-center gap-1">
                        <div className="flex-grow">
                            <Select
                                value={activeAgentId || 'none'}
                                onValueChange={handleAgentSelect}
                                disabled={!currentSessionId}
                            >
                                <SelectTrigger id="agent-selector" className="w-full text-xs">
                                    <SelectValue placeholder="No agent selected" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None (Manual)</SelectItem>
                                    {agents.filter(a => a.is_active).map(agent => (
                                        <SelectItem key={agent.id} value={agent.id}>
                                            {agent.name} {agent.is_default ? '★' : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {activeAgentId && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 flex-shrink-0"
                                    onClick={handleReSyncAgent}
                                    title="Re-sync from agent"
                                >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 flex-shrink-0"
                                    onClick={handleDetachAgent}
                                    title="Detach from agent (keep current values)"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </Button>
                            </>
                        )}
                    </div>
                    
                </div>

                {/* Expand/Collapse Toggle */}
                <button
                    type="button"
                    className="flex items-center gap-1 w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                >
                    {showAdvanced ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    <span className="font-medium">
                        {showAdvanced ? 'Hide configuration' : configSummary}
                    </span>
                </button>

                {/* Advanced Settings — collapsible */}
                {showAdvanced && (
                    <>
                        <ModelSelector
                            value={selectedModelId}
                            onChange={(modelId) => handleDefaultsUpdate({ model_id: modelId || undefined })}
                            disabled={!currentSessionId}
                            label="Model"
                        />

                        <div className="space-y-1">
                            <Label htmlFor="reasoning-effort-selector" className="text-xs">Reasoning Effort</Label>
                            <Select
                                value={currentSessionDetails?.reasoning_effort || 'default'}
                                onValueChange={(value) => handleDefaultsUpdate({ reasoning_effort: value === 'default' ? null : value as ReasoningEffort })}
                                disabled={!currentSessionId}
                            >
                                <SelectTrigger id="reasoning-effort-selector" className="w-full text-xs">
                                    <SelectValue placeholder="Select reasoning effort" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="default">Default</SelectItem>
                                    {ALL_REASONING_EFFORTS.map(effort => (
                                        <SelectItem key={effort} value={effort} className="capitalize">{effort}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1">
                            <Label htmlFor="system-prompt-selector" className="text-xs">System Prompt</Label>
                            <div className="flex items-center space-x-1">
                                <div className="flex-grow">
                                    <Select
                                        value={selectedSystemPromptId}
                                        onValueChange={handleSystemPromptChange}
                                        disabled={!currentSessionId || isSystemPromptsLoading}
                                    >
                                        <SelectTrigger id="system-prompt-selector" className="w-full text-xs">
                                            <SelectValue placeholder="Select system prompt" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Default</SelectItem>
                                            {systemPrompts.map(prompt => (
                                                <SelectItem key={prompt.id} value={prompt.id}>{prompt.prompt_name} {prompt.is_default && '(Default)'}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {selectedSystemPrompt?.url && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 flex-shrink-0"
                                        onClick={() => {
                                            if (selectedSystemPrompt.url) {
                                                window.open(selectedSystemPrompt.url, '_blank')
                                            }
                                        }}
                                    >
                                        <Link className="h-4 w-4" />
                                        <span className="sr-only">Open Prompt URL</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="initial-template-selector" className="text-xs">Initial Prompt Template</Label>
                            <Select
                                value={currentSessionDetails?.default_initial_context_template_id || 'none'}
                                onValueChange={(value) => handleDefaultsUpdate({ default_initial_context_template_id: value === 'none' ? null : value })}
                                disabled={!currentSessionId || isTemplatesLoading}
                            >
                                <SelectTrigger id="initial-template-selector" className="w-full text-xs">
                                    <SelectValue placeholder="Select initial template" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {contextTemplates.map(template => (
                                        <SelectItem key={template.id} value={template.id}>{template.template_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="followup-template-selector" className="text-xs">Follow-up Prompt Template</Label>
                            <Select
                                value={currentSessionDetails?.default_followup_context_template_id || 'none'}
                                onValueChange={(value) => handleDefaultsUpdate({ default_followup_context_template_id: value === 'none' ? null : value })}
                                disabled={!currentSessionId || isTemplatesLoading}
                            >
                                <SelectTrigger id="followup-template-selector" className="w-full text-xs">
                                    <SelectValue placeholder="Select follow-up template" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {contextTemplates.map(template => (
                                        <SelectItem key={template.id} value={template.id}>{template.template_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default SessionSettings;